/* eslint-disable no-console */
import { createRequestOptions } from '@factories/requestPayloadFactory'
import { baseURL, headers, latticeBaseURL, latticeHeaders, PublicationStatusBasedOnEnv } from '@helpers/strapiHelpers'
import {
  LatticeFetchByIdOptions,
  LatticeFetchOptions,
  StrapiFetchByIdOptions,
  StrapiFetchOptions,
} from '@interfaces/strapi'
import { noticeError } from '@utils/newrelic'
import { buildQueryParams } from '@utils/queryparamsBuilder'
import { fetchWithTimeout } from '@utils/utils'
import { HTTPRequestMethods } from '@enums/HTTPRequestMethods'
import { HeaderContentType } from '@enums/HeaderContentType'
import { LatticeEntryState, Order } from '@enums/strapiTypesAndOperators'
import { DEEP_POPULATE } from '@constants/populateTypes'
import { DEFAULT_CMS_TIMEOUT } from '@constants/times'
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_START_INDEX } from '@constants/strapi'

export const getContentFromCMS = async ({
  contentType,
  populate = DEEP_POPULATE,
  filters,
  sort,
  order = Order.DESCENDING,
  fields,
  limit,
  start = DEFAULT_PAGE_START_INDEX,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  publicationState = PublicationStatusBasedOnEnv,
  locale,
  includeMeta = false,
  getFirstEntry = false,
  timeout = DEFAULT_CMS_TIMEOUT,
}: StrapiFetchOptions) => {
  let data, meta
  const params = buildQueryParams({
    populate,
    filters,
    sort,
    order,
    fields,
    limit,
    start,
    page,
    pageSize,
    publicationState,
    locale,
  })
  const url = decodeURIComponent(`${baseURL}/${contentType}?${params.toString()}`)
  console.info('[Carbon Analytics] Fetching CMS data, URL: ', url)

  try {
    const response = await fetchWithTimeout(
      url,
      createRequestOptions({ method: HTTPRequestMethods.GET, headers, timeout })
    )

    if (!response.ok) {
      throw new Error(`[Carbon Analytics] HTTP error Status: ${response.status}`)
    }

    const responseData = await response.json()
    data = getFirstEntry ? responseData?.data?.[0]?.attributes : responseData?.data
    meta = responseData?.meta
  } catch (error) {
    console.error('[Carbon Analytics] CMS data fetch error:', error)
    noticeError({ err: error, customAttributes: { url } })
  }

  return includeMeta ? { data, meta } : data
}

export const getContentFromLattice = async ({
  contentType,
  populate = ['all'],
  filters,
  sort,
  order = Order.DESCENDING,
  locale,
  includeMeta = false,
  getFirstEntry = false,
  timeout = DEFAULT_CMS_TIMEOUT,
  status = LatticeEntryState.Published,
}: LatticeFetchOptions) => {
  let data, meta
  const params = buildQueryParams({
    populate,
    filters,
    sort,
    order,
    locale,
    status,
  })
  const url = decodeURIComponent(`${latticeBaseURL}/${contentType}?${params.toString()}`)
  console.info('[Carbon Analytics] Fetching CMS data, URL: ', url)

  try {
    const response = await fetchWithTimeout(
      url,
      createRequestOptions({ method: HTTPRequestMethods.GET, headers: latticeHeaders, timeout })
    )

    if (!response.ok) {
      throw new Error(`[Carbon Analytics] HTTP error Status: ${response.status}`)
    }

    const responseData = await response.json()
    data = getFirstEntry ? responseData?.data?.[0] : responseData?.data
    meta = responseData?.meta
  } catch (error) {
    console.error('[Carbon Analytics] CMS data fetch error:', error)
    noticeError({ err: error, customAttributes: { url } })
  }

  return includeMeta ? { data, meta } : data
}

export const getEntryFromCMSUsingId = async ({
  contentType,
  contentId,
  populate = DEEP_POPULATE,
  fields,
  publicationState = PublicationStatusBasedOnEnv,
  includeMeta = false,
  timeout = DEFAULT_CMS_TIMEOUT,
}: StrapiFetchByIdOptions) => {
  let data, meta
  const params = buildQueryParams({ populate, fields, publicationState })
  const url = decodeURIComponent(`${baseURL}/${contentType}/${contentId}?${params.toString()}`)
  console.info('[Carbon Analytics] Fetching CMS data URL: ', url)

  try {
    const response = await fetchWithTimeout(
      url,
      createRequestOptions({ method: HTTPRequestMethods.GET, headers, timeout })
    )

    if (!response.ok) {
      throw new Error(`[Carbon Analytics] HTTP error Status: ${response.status}`)
    }

    const responseData = await response.json()
    data = responseData?.data
    meta = responseData?.meta
  } catch (error) {
    console.error('[Carbon Analytics] CMS data fetch error:', error)
    noticeError({ err: error, customAttributes: { url } })
  }

  return includeMeta ? { data, meta } : data
}

export const getEntryFromLatticeUsingId = async ({
  contentType,
  contentId,
  populate = ['all'],
  status = LatticeEntryState.Published,
  includeMeta = false,
  timeout = DEFAULT_CMS_TIMEOUT,
}: LatticeFetchByIdOptions) => {
  let data, meta
  const params = buildQueryParams({ populate, status })
  const url = decodeURIComponent(`${latticeBaseURL}/${contentType}/${contentId}?${params.toString()}`)
  console.info('[Carbon Analytics] Fetching CMS data URL: ', url)

  try {
    const response = await fetchWithTimeout(
      url,
      createRequestOptions({ method: HTTPRequestMethods.GET, headers: latticeHeaders, timeout })
    )

    if (!response.ok) {
      throw new Error(`[Carbon Analytics] HTTP error Status: ${response.status}`)
    }

    const responseData = await response.json()
    data = responseData?.data
    meta = responseData?.meta
  } catch (error) {
    console.error('[Carbon Analytics] CMS data fetch error:', error)
    noticeError({ err: error, customAttributes: { url } })
  }

  return includeMeta ? { data, meta } : data
}

export const postToStrapi = async ({ contentType, data }) => {
  try {
    if (!contentType || !data) {
      throw new Error('Content type and data are required.')
    }

    const authToken = process.env.AUTHORIZATION_TOKEN
    const payload = { data }

    const response = await fetch(`${process.env.ACKO_CMS_URL}/api/${contentType}/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': HeaderContentType.JSON,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(`Error Posting Data: ${errorData.error?.message || response.statusText}`)
    }

    const responseData = await response.json()
    return responseData
  } catch (error) {
    console.error('Error posting data to strapi', error)
    throw error
  }
}

export const updateStrapiEntry = async ({ contentType, id, data }) => {
  try {
    if (!contentType || !id || !data) {
      throw new Error('Content type, ID, and data are required.')
    }

    const authToken = process.env.AUTHORIZATION_TOKEN
    const payload = { data }

    const response = await fetch(`${process.env.ACKO_CMS_URL}/api/${contentType}/${id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': HeaderContentType.JSON,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(`Error Updating Data: ${errorData.error?.message || response.statusText}`)
    }

    const responseData = await response.json()
    return responseData
  } catch (error) {
    console.error('Error updating data in strapi: ', error)
    throw error
  }
}
